import {useEffect, useRef} from 'react';
import {defineMessages, useIntl} from 'react-intl';

import {search, cancel} from '@sail/icons/react/Icon';
import {Action, view, Icon} from '@sail/ui';

import {COLORS} from 'src/v2/util/color';
import {Text} from 'src/v2/components/common';

const messages = defineMessages({
  search: {
    id: 'crypto.payins.v2.wallet_select.search_input.placeholder',
    description:
      'Placeholder text for the search input in the wallet select grid',
    defaultMessage: 'Search for a wallet',
  },
  searchAlt: {
    id: 'crypto.payins.v2.wallet_select.search_input.placeholder_alt',
    description: 'Alt for the search input in the wallet select grid',
    defaultMessage: 'Search wallets',
  },
  clear: {
    id: 'crypto.payins.v2.wallet_select.search_input.clear',
    description: 'Label for the clear item in the wallet select grid',
    defaultMessage: 'Clear',
  },
});

interface Props {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  onClose: () => void;
  contentVisible: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  // Optional ref to the input element, used by keyboard navigation to
  // return focus to the search input when navigating out of the grid.
  inputRef?: React.RefObject<HTMLInputElement>;
}

export const SearchInput = ({
  searchQuery,
  onSearchQueryChange,
  onClose,
  contentVisible,
  onKeyDown,
  onFocus,
  inputRef: externalInputRef,
}: Props) => {
  const intl = useIntl();
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  return (
    <view.div
      css={{
        stack: 'x',
        alignY: 'center',
        gap: 'medium',
      }}
    >
      <view.div
        css={{
          stack: 'x',
          alignY: 'center',
          gap: 'small',
          color: COLORS.textDisabledOnLight,
          distribute: 'space-between',
          backgroundColor: 'white',
          padding: 'space.175',
          borderWidth: 'small',
          borderStyle: 'solid',
          borderColor: COLORS.borderOnLight,
          borderRadius: 'small',
          ':focus-within': {
            boxShadow: '0 0 0 4px rgba(50, 151, 211, 0.3)',
          },
          transform: contentVisible ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'left',
          transition: 'transform 150ms ease-in-out',
        }}
      >
        <Icon icon={search} size="small" />
        <view.input
          ref={inputRef}
          css={{
            border: 'none',
            outline: 'none',
            width: 'fill',
            lineHeight: '16px',
            fontSize: '14px',
            fontWeight: 500,
            color: COLORS.textPrimary,
            letterSpacing: '0px',
          }}
          placeholder={intl.formatMessage({...messages.search})}
          aria-label={intl.formatMessage({...messages.searchAlt})}
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
        />

        {searchQuery ? (
          // display: inline-flex to make the <a> tag have the same height and width as the child
          // height: fit doesn't work somehow
          <Action
            onPress={() => {
              onSearchQueryChange('');
            }}
            css={{
              display: 'inline-flex',
            }}
          >
            <Text type="captionEmphasized">
              {intl.formatMessage({...messages.clear})}
            </Text>
          </Action>
        ) : null}
      </view.div>
      <Action
        css={{
          width: 'fit',
          opacity: contentVisible ? 1 : 0,
          transition: 'opacity 150ms ease-in-out',
        }}
        onPress={onClose}
      >
        <Icon icon={cancel} size="xsmall" css={{color: COLORS.textPrimary}} />
      </Action>
    </view.div>
  );
};
